import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useLayoutEffect, useMemo } from 'react';
import * as THREE from 'three';
export function ProgressivePathConnectorLine({ points, color, opacity, depthTest, activation }) {
    const resources = useMemo(() => {
        const geometry = new THREE.BufferGeometry();
        const vertexCount = (points.length + 63) * 2;
        geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(vertexCount * 3), 3).setUsage(THREE.DynamicDrawUsage));
        geometry.setAttribute('color', new THREE.BufferAttribute(new Float32Array(vertexCount * 3), 3).setUsage(THREE.DynamicDrawUsage));
        const material = new THREE.LineBasicMaterial({ vertexColors: true, transparent: true });
        return { geometry, material, line: new THREE.LineSegments(geometry, material) };
    }, [points.length]);
    useLayoutEffect(() => {
        if (points.length < 2 || !points.every((point) => point.length === 3 && point.every(Number.isFinite))
            || !Number.isFinite(activation.progress01))
            throw new Error('Invalid progressive connector path.');
        const lengths = points.slice(1).map((point, index) => Math.hypot(...point.map((value, axis) => value - points[index][axis])));
        const total = lengths.reduce((sum, length) => sum + length, 0);
        if (!Number.isFinite(total))
            throw new Error('Progressive connector path length must be finite.');
        const sample = (fraction) => {
            let distance = fraction * total;
            for (let index = 0; index < lengths.length; index++) {
                const length = lengths[index];
                if (distance <= length || index === lengths.length - 1) {
                    const t = length === 0 ? 0 : distance / length;
                    return points[index].map((value, axis) => value + (points[index + 1][axis] - value) * t);
                }
                distance -= length;
            }
            return points[0];
        };
        const progress = Math.max(0, Math.min(1, activation.progress01));
        const front = activation.direction === 'to-from' ? 1 - progress : progress;
        const positions = resources.geometry.getAttribute('position');
        const colors = resources.geometry.getAttribute('color');
        const idle = new THREE.Color(color);
        const active = new THREE.Color(activation.color);
        let cumulative = 0;
        const corners = lengths.slice(0, -1).map((length) => {
            cumulative += length;
            return total === 0 ? 0 : cumulative / total;
        });
        const boundaries = [...Array.from({ length: 65 }, (_, index) => index / 64), ...corners, front].sort((a, b) => a - b);
        for (let segment = 0; segment < boundaries.length - 1; segment++) {
            const from = boundaries[segment];
            const to = boundaries[segment + 1];
            const isActive = activation.direction === 'to-from' ? from >= front : to <= front;
            for (let endpoint = 0; endpoint < 2; endpoint++) {
                const fraction = endpoint === 0 ? from : to;
                const tint = isActive ? active : idle;
                positions.setXYZ(segment * 2 + endpoint, ...sample(fraction));
                colors.setXYZ(segment * 2 + endpoint, tint.r, tint.g, tint.b);
            }
        }
        positions.needsUpdate = true;
        colors.needsUpdate = true;
        resources.material.opacity = opacity;
        resources.material.depthTest = depthTest;
    }, [points, color, opacity, depthTest, activation, resources]);
    useEffect(() => () => { resources.geometry.dispose(); resources.material.dispose(); }, [resources]);
    return _jsx("primitive", { object: resources.line, dispose: null, frustumCulled: false });
}
